import { Map as ImMap, List, type RecordOf } from 'immutable';

import type { institutionsTypes } from 'app-components/flux/institutions';
import { getInstitutionsById } from 'app-components/flux/institutions/institutionsSelectors';
import {
  getAggregatorFromChannel,
  getStaticInstitutionFromAggregator,
} from 'app-components/flux/institutions/institutionsUtils';
import { createAppSelector } from 'app-components/utils/core/reselect';
import type RootState from 'app-components/utils/redux-store/rootState';

import { accountsSelectors } from '../accounts';
import type { Account } from '../accounts/accountsTypes';
import {
  type AggErrorDismissalKey,
  type AggStatus,
  INSTITUTION_LOGINS_REDUCER_KEY,
  type InstitutionLoginProps,
} from './institutionLoginsTypes';
import { isErrorType } from './institutionLoginsUtils';

type State = Pick<RootState, typeof INSTITUTION_LOGINS_REDUCER_KEY>;

export type LoginIssue =
  | { kind: 'fiIssue'; fiIssueType: institutionsTypes.FiIssueType }
  | { kind: 'aggStatus'; aggStatus: AggErrorDismissalKey };

export type LoginsWithIssuesResult = {
  loginsWithIssues: RecordOf<InstitutionLoginProps>[];
  issuesByLoginId: ImMap<string, LoginIssue>;
};

export const getInstitutionLoginsStore = (state: State) => state[INSTITUTION_LOGINS_REDUCER_KEY];

export const getInstitutionLoginsById = (state: State) => getInstitutionLoginsStore(state).resourcesById;
export const getLoadPending = (state: State) => getInstitutionLoginsStore(state).loadPending;
export const getLastSyncDate = (state: State) => getInstitutionLoginsStore(state).lastSyncDate;

export const getInstitutionLoginForId = (state: State, id: string) => getInstitutionLoginsById(state)?.get(id);

export const getIsRefreshingAll = (state: State) => getInstitutionLoginsStore(state).isRefreshingAll;
export const getIdsRefreshing = (state: State) => getInstitutionLoginsStore(state).idsRefreshing;

export const getIsRefreshingSome = (state: State) => getIdsRefreshing(state).size > 0;

export const getIdsRefreshingWithUserActionsPending = (state: State) =>
  getInstitutionLoginsStore(state).idsRefreshingWithUserActionsPending;

export const getIdsRefreshingWithCancelledUserActions = (state: State) =>
  getInstitutionLoginsStore(state).idsRefreshingWithCancelledUserActions;

export const getCredentialBlobsForRefreshingLogins = (state: State) =>
  getInstitutionLoginsStore(state).credentialBlobsForRefreshingLogins;

export const getCredentialsForRefreshingLogins = (state: State) =>
  getInstitutionLoginsStore(state).credentialsForRefreshingLogins;

export const getDismissedFiIssuesByLoginId = (state: State) =>
  getInstitutionLoginsStore(state).dismissedFiIssuesByLoginId;

export const getDismissedAggStatusesByLoginId = (state: State) =>
  getInstitutionLoginsStore(state).dismissedAggStatusesByLoginId;

export const hasUserActionBeenQueued = (state: State, institutionLoginId: string) =>
  getIdsRefreshingWithUserActionsPending(state).has(institutionLoginId) ||
  getIdsRefreshingWithCancelledUserActions(state).has(institutionLoginId);

export const getActiveConnectedInstitutionLogins = createAppSelector(
  getInstitutionLoginsById,
  accountsSelectors.getAccountsById,
  (institutionLoginsById, accountsById) =>
    institutionLoginsById.filter(
      (login) => login?.aggregators && accountsById.find((account) => account?.institutionLoginId === login.id),
    ),
);

//  for useLoadState compatibility
export const getResourcesById = (state: State) => getInstitutionLoginsStore(state).resourcesById;
export const getIsLoading = (state: State) => getInstitutionLoginsStore(state).isLoading;
export const getError = (state: State) => getInstitutionLoginsStore(state).error;

export const isUserActionPending = createAppSelector(
  getIdsRefreshingWithUserActionsPending,
  (_, props) => (props ? props.institutionLoginIdId : null),
  (idsRefreshingWithUserActionsPending, institutionLoginId) =>
    institutionLoginId == null
      ? idsRefreshingWithUserActionsPending.size > 0
      : idsRefreshingWithUserActionsPending.has(institutionLoginId),
);
export const wasUserActionCancelled = (state: State, institutionLoginId: string) =>
  getIdsRefreshingWithCancelledUserActions(state).has(institutionLoginId);

export const getInstitutionLoginsByName = createAppSelector(getInstitutionLoginsById, (institutionLoginsById) =>
  !institutionLoginsById
    ? null
    : institutionLoginsById.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }),
      ),
);

type AccountsByInstitutionLoginId = ImMap<string, List<Account>>;

const getLoginIssue = (
  login: RecordOf<InstitutionLoginProps>,
  accountsByInstitutionLoginId: AccountsByInstitutionLoginId | undefined,
  dismissedFiIssuesByLoginId: ImMap<string, institutionsTypes.FiIssueType>,
  dismissedAggStatusesByLoginId: ImMap<string, AggErrorDismissalKey>,
): LoginIssue | undefined => {
  if (login.isDeleted || login.isConnected !== true) {
    return undefined;
  }

  const accounts = accountsByInstitutionLoginId?.get(login.id);
  if (!accounts || accounts.size === 0) {
    return undefined;
  }

  const hasOpenAccounts = accounts.some((account) => !account?.isClosed && !account?.isDeleted);
  if (!hasOpenAccounts) {
    return undefined;
  }

  const fiIssue = login.fiIssue;
  if (fiIssue?.issueType === 'INFO') {
    const dismissedType = dismissedFiIssuesByLoginId?.get(login.id);
    if (dismissedType !== fiIssue.issueType) {
      return { kind: 'fiIssue', fiIssueType: fiIssue.issueType };
    }
  }

  const loginAggStatus = login.aggregators?.get(0)?.aggStatus;
  const dismissedAggStatus = dismissedAggStatusesByLoginId?.get(login.id);
  const hasLoginAggError = Boolean(loginAggStatus && isErrorType(loginAggStatus));
  if (hasLoginAggError && loginAggStatus && dismissedAggStatus !== loginAggStatus) {
    return { kind: 'aggStatus', aggStatus: loginAggStatus };
  }

  const hasAccountAggError = Boolean(
    accounts &&
      !hasLoginAggError &&
      accounts.some((account) => {
        const accountAggStatus = account.aggregators?.get(0)?.aggStatus;
        return Boolean(accountAggStatus && isErrorType(accountAggStatus as AggStatus));
      }),
  );
  if (hasAccountAggError && dismissedAggStatus !== 'ACCOUNT_AGG_ERROR') {
    return { kind: 'aggStatus', aggStatus: 'ACCOUNT_AGG_ERROR' };
  }

  return undefined;
};

export const getLoginsWithIssues = createAppSelector(
  getInstitutionLoginsByName,
  accountsSelectors.getAccountsByInstitutionLoginId,
  getDismissedFiIssuesByLoginId,
  getDismissedAggStatusesByLoginId,
  (
    institutionLoginsByName,
    accountsByInstitutionLoginId,
    dismissedFiIssuesByLoginId,
    dismissedAggStatusesByLoginId,
  ): LoginsWithIssuesResult => {
    if (!institutionLoginsByName) {
      return { loginsWithIssues: [], issuesByLoginId: ImMap() };
    }

    let issuesByLoginId = ImMap<string, LoginIssue>();
    const loginsWithIssues: RecordOf<InstitutionLoginProps>[] = [];

    institutionLoginsByName.forEach((login) => {
      const issue = getLoginIssue(
        login,
        accountsByInstitutionLoginId,
        dismissedFiIssuesByLoginId,
        dismissedAggStatusesByLoginId,
      );
      if (issue) {
        issuesByLoginId = issuesByLoginId.set(login.id, issue);
        loginsWithIssues.push(login);
      }
    });

    return { loginsWithIssues, issuesByLoginId };
  },
);

export const isInstitutionLoginAmbiguous = createAppSelector(
  getInstitutionLoginsById,
  (_, props) => props.institutionLoginId,
  (institutionLoginsById: ImMap<string, InstitutionLoginProps>, institutionLoginId) => {
    if (!institutionLoginId) {
      return undefined;
    }
    const institutionLogin = institutionLoginsById.get(institutionLoginId);
    if (institutionLogin) {
      const ambiguousInstitutionLogin = institutionLoginsById.find(
        (iInstitutionLogin: InstitutionLoginProps) =>
          iInstitutionLogin?.institutionId === institutionLogin.institutionId &&
          iInstitutionLogin?.id !== institutionLogin.id,
      );
      return ambiguousInstitutionLogin !== undefined;
    }
    return undefined;
  },
);

export const getTrackingAggregator = createAppSelector(getInstitutionLoginForId, (institutionLogin) => {
  if (institutionLogin?.channel) {
    if (institutionLogin.channel.startsWith('FDS')) {
      return 'FDS';
    }
    if (institutionLogin.channel.startsWith('FDP')) {
      return 'FDP';
    }
    if (institutionLogin.channel.startsWith('FINICITY')) {
      return 'FINICITY';
    }
    if (institutionLogin.channel.startsWith('PLAID')) {
      return 'PLAID';
    }
    if (institutionLogin.channel.startsWith('STRIPE')) {
      return 'STRIPE';
    }
  }
  return institutionLogin?.channel ?? 'UNKNOWN';
});

// This selector returns all "pure" manual accounts. A pure manual is an account that either does not belong to an
// institution-login or belongs to an "unconnected" institution-login. While QCS supports accounts being grouped into
// unconnected-logins, it is only used by Windows to group all manual accounts into a single unconnectted
// intitution-login (this isn't required but is a holdover from Windows syncing to Mint).
//
export const getPureManualAccounts = createAppSelector(
  getInstitutionLoginsById,
  accountsSelectors.getAccountsByInstitutionLoginId,
  (institutionLoginsById, accountsByInstitutionId) => {
    const pureManualAccounts = accountsByInstitutionId.reduce<Account[]>((list, accounts, institutionLoginId) => {
      const institutionLogin = institutionLoginsById.get(institutionLoginId);

      // Special Case: Stripe login has no institutionId but is not a manual account.
      const isStripeAccount = institutionLogin?.channel === 'STRIPE' || institutionLogin?.channel === 'STRIPE_TEST';

      if (
        (Number(institutionLoginId) === 0 ||
          (institutionLogin &&
            !institutionLogin.isConnected &&
            (Number(institutionLogin.institutionId) === 0 || !institutionLogin.aggregators?.size))) &&
        !isStripeAccount
      ) {
        accounts.forEach((account: Account) => list.push(account));
      }
      return list;
    }, []);
    return List(pureManualAccounts);
  },
);

export const getFinancialInstitutionsPendo = createAppSelector(
  accountsSelectors.getAccountsById,
  getInstitutionLoginsById,
  (accounts, logins) =>
    accounts.reduce<string[]>((results, account) => {
      const login = account.institutionLoginId !== undefined ? logins.get(account.institutionLoginId) : undefined;
      account.aggregators?.forEach?.((aggregator) => {
        const financialInstitutionPendo = `${aggregator.channel || ''}:${login?.id || ''}:${
          aggregator.aggStatus || ''
        }:${aggregator.aggStatusCode || ''}:${login?.name || ''}`;
        if (!results.includes(financialInstitutionPendo)) {
          results.push(financialInstitutionPendo);
        }
      });
      return results;
    }, []),
);

export const getFiIssueForLogin = createAppSelector(
  (state: State, id: string) => getInstitutionLoginForId(state, id),
  getDismissedFiIssuesByLoginId,
  // The dismissed prop is an override to include dismissed fiIssues in the selector
  (_state: State, _id: string, showDismissed?: boolean) => showDismissed,
  (institutionLogin, dismissedFiIssuesByLoginId, showDismissed) => {
    const fiIssue = institutionLogin?.fiIssue;
    if (fiIssue && (dismissedFiIssuesByLoginId?.get(institutionLogin.id) !== fiIssue.issueType || showDismissed)) {
      return fiIssue;
    }
    return undefined;
  },
);

export const getInstitutionLoginsByChannel = createAppSelector(
  [getInstitutionLoginsById, (_state: State, channel) => channel],
  (institutionLoginsById, channel) => institutionLoginsById.filter((login) => login.channel === channel),
);

export const getInstitutionForInstitutionLogin = createAppSelector(
  getInstitutionLoginsById,
  (_state: State, id: string | undefined) => id,
  getInstitutionsById,
  (institutionLoginsById, id, institutionsById) => {
    if (!id) return undefined;
    const institutionLogin = institutionLoginsById.get(id);
    if (!institutionLogin) return undefined;
    if (institutionLogin.institutionId) {
      return institutionsById.get(institutionLogin.institutionId);
    }
    if (institutionLogin.isConnected && institutionLogin.channel && institutionLogin.institutionId === null) {
      const aggregator = getAggregatorFromChannel(institutionLogin.channel);
      return getStaticInstitutionFromAggregator(aggregator);
    }
    return undefined;
  },
);

export const isRefreshFrequencySupported = createAppSelector(
  (state: State, id?: string) => getInstitutionLoginForId(state, id || ''),
  (institutionLogin) => ['FDP_WSI_OAUTH', 'FDP_DEFAULT', 'FINICITY', 'PLAID'].includes(institutionLogin?.channel || ''),
);

export const getFiMigrationBannerDismissed = (state: State): boolean =>
  getInstitutionLoginsStore(state).fiMigrationBannerDismissed;
